/**
 * Network Workbench - Unified Build/Run Interface
 * Tabbed interface for building and running networks.
 *
 * End-to-end build (UI → Brain → realtime UI):
 * 1. User on /network/new sends a prompt from BuildTab.
 * 2. BuildTab POST /networks creates the DynamoDB network row, then calls
 *    ``startBrainDeployment(networkId, prompt)`` (REST).
 * 3. ``start_deployment_rest_handler`` starts the Brain Step Function with
 *    userId + tenantId.
 * 4. Brain Lambdas publish canonical envelopes via ``status_broadcaster``
 *    (AppSync Events → DynamoDB). Subscribers on the
 *    ``/default/agent/{scope}/{networkId}/default`` channel receive the
 *    stream in real time (mirrored into ``websocket-message`` DOM events).
 * 5. On success, onNetworkUpdate refreshes the network (interface, runtimeConfig)
 *    for Run/Agents tabs.
 *
 * Transport: AppSync Events only. The legacy API Gateway WebSocket stack
 * has been retired — ``connectionId`` is no longer plumbed anywhere in the
 * build/run paths.
 */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import ReleaseNetworkDialog from './components/ReleaseNetworkDialog.jsx';
import SaveSnapshotDialog from './components/SaveSnapshotDialog.jsx';
import DeleteNetworkDialog from './components/DeleteNetworkDialog.jsx';
import { Tab } from '@headlessui/react';
import {
  Hammer,
  Bot,
  Play,
  BarChart3,
  Settings,
  GitBranch,
  ListChecks,
  Database,
  Cable,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';
import BuildTab from './components/BuildTab.jsx';
import RunTab from './components/RunTab.jsx';
import AgentsTab from './components/AgentsTab.jsx';
import SettingsTab from './components/SettingsTab.jsx';
import SelfImprovementTab from './components/SelfImprovementTab.jsx';
import UsageTab from './components/UsageTab.jsx';
import LifecycleTab from './components/LifecycleTab.jsx';
import DataPlaneTab from './components/DataPlaneTab.jsx';
import SecretsCatalog from '@stepflow/ui-shared/components/secrets/SecretsCatalog.jsx';
import SkillsCatalog from '@stepflow/ui-shared/components/skills/SkillsCatalog.jsx';
import WorkbenchExecutionsTab from './components/WorkbenchExecutionsTab.jsx';
import WorkbenchHeader from './components/WorkbenchHeader.jsx';
import { useAppStore } from '@/stores/useAppStore';
import { canAccessWorkbenchBuild, canDeleteNetworkRole } from '@/utils/stepflowRoles';
import { getReleaseBannerConfig } from '@/utils/releaseBanner';
import { getRestApiBearerToken, appSyncAgentScopeFromBearerToken } from '@/services/apiToken';
import { useAgentChannel, appsyncChannelScope, agentChannelEventKey } from '@/hooks/useAgentChannel';
import { useWorkbenchChannel } from '@/hooks/useWorkbenchChannel';
import {
  dispatchWorkbenchRealtime,
  normalizeAppSyncWorkbenchPayload,
} from '@/services/workbenchRealtime';
import { fetchNetworkChannelEvents } from '@/services/networkChannelEvents';
import { startBrainDeployment } from '@/services/brainBuildApi';
import { listNetworkReleases } from '@/services/networkReleasesApi';
import { evaluateRestoreEvents } from '@stepflow/ui-shared/utils/restoreEvents';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function isNetworkDeployedForTabs(status) {
  const s = (status || '').toString().toUpperCase();
  return s === 'ACTIVE' || s === 'RELEASED';
}

export default function NetworkWorkbench() {
  const { id: networkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, activeTenantId, workspaceRole } = useAuth();
  const { showToast } = useToast();
  const canBuild = canAccessWorkbenchBuild(workspaceRole);
  const canDeleteNw = canDeleteNetworkRole(workspaceRole);
  const { networks, refreshNetworks, refreshAgenticNetworks } = useOutletContext() || {};

  /** Declared before AppSync hooks so we can defer the collab channel off the Build tab. */
  const [selectedTab, setSelectedTab] = useState(0); // 0 Build, 1 Executions, 2 Agents, 3 Run, 4 Lifecycle, 5 Data Plane, 6 Connections, 7 Skills, 8 Usage, 9 Settings, 10 Improve
  // One-shot revise prompt handed from the Improve tab → Build tab (§6b). Bumped each
  // time so re-selecting the same suggestions re-prefills (a new object identity).
  const [reviseSeedPrompt, setReviseSeedPrompt] = useState('');

  // Connections-tab index + a one-shot "auto-open this tool's creator" hint.
  // Set when the agent tool picker's instance list is empty and the user clicks
  // "Create in Connections" — we jump to the Connections tab and tell
  // SecretsCatalog which creator to open. Cleared once consumed.
  const CONNECTIONS_TAB_INDEX = 6;
  const AGENTS_TAB_INDEX = 2;
  const [connectionsAutoOpenTool, setConnectionsAutoOpenTool] = useState(null);
  // Round-trip: after a connection is created in Connections, carry it back to
  // the tool picker to auto-assign. One-shot; cleared once the picker consumes
  // it. ``{ tool, secretId, secretName }``.
  const [pendingToolAssign, setPendingToolAssign] = useState(null);
  const navigateToConnections = useCallback(({ tool } = {}) => {
    setConnectionsAutoOpenTool(tool || null);
    setSelectedTab(CONNECTIONS_TAB_INDEX);
  }, []);
  const handleConnectionCreated = useCallback((created) => {
    // Created in Connections → return to Agents tab + hand the new connection to
    // the picker to assign. (Cancel never calls this, so nothing is assigned on
    // cancel.) Panels stay mounted (unmount=false), so the same agent is still
    // selected when we return.
    if (created?.tool && created?.secretId) {
      setPendingToolAssign(created);
    }
    setSelectedTab(AGENTS_TAB_INDEX);
  }, []);

  const appSyncChannelId =
    networkId && networkId !== 'new' ? networkId : undefined;
  /** Match authorizer + Brain ``scope_segment`` from the same bearer used on the WebSocket. */
  const workspaceChannelScope = useMemo(() => {
    const fromToken = appSyncAgentScopeFromBearerToken(getRestApiBearerToken());
    return fromToken || appsyncChannelScope(activeTenantId, user?.sub);
  }, [activeTenantId, user?.sub]);
  const appSyncScope = workspaceChannelScope;
  // Workbench authorizer compares channel tenant to JWT ``tid`` — prefer the same
  // token-derived org id so we never subscribe with ``activeTenantId`` that lags
  // the session JWT after a workspace switch (that yields UnauthorizedException).
  const workbenchTenantId = useMemo(() => {
    if (!canBuild) return '';
    const s = (workspaceChannelScope || '').trim();
    const personal = s.startsWith('user-') || s.startsWith('user_');
    if (s && !personal) return s;
    return (activeTenantId || '').trim();
  }, [canBuild, workspaceChannelScope, activeTenantId]);
  // Workbench is the creator/build view — no runtime network-tenant selected.
  // RunTab will subscribe with the concrete pool id once a run is started.
  const appSyncPoolId = 'default';
  const {
    connected: appSyncConnected,
    error: appSyncError,
    messages: appSyncMessages,
  } = useAgentChannel(appSyncChannelId, appSyncScope, appSyncPoolId);

  // Collaborative Workbench channel — tenant-wide presence + field locks +
  // FIELD_UPDATED beacons. Disabled on the Build tab so we do not keep a third
  // AppSync WebSocket + presence ``POST …/event`` open while only streaming
  // Brain progress on the agent channel (see useWorkbenchChannel ``enabled``).
  const workbenchCollabEnabled = canBuild && selectedTab !== 0;
  const {
    connected: workbenchConnected,
    presence: workbenchPresence,
    events: workbenchChannelEvents,
  } = useWorkbenchChannel(appSyncChannelId, workbenchTenantId, user?.sub, {
    enabled: workbenchCollabEnabled,
  });

  // Dispatch build-progress envelopes by STABLE dedupe KEY, not array index. ``useAgentChannel``
  // trims its buffer with ``.slice(-SOFT_CAP)`` (front-trim), which SHIFTS array indices — an
  // absolute index cursor then silently SKIPS a window of events under a burst (DistributedMap
  // fires 10-20 agents/skills concurrently → buffer crosses the cap mid-build → dropped
  // BUILD_AGENT_ROW/SKILL_ROW → "20 skills ended at 10", counts jumping). Keying off the same
  // ``agentChannelEventKey`` the channel dedupes with is trim-proof + idempotent + order-safe.
  const dispatchedKeysRef = useRef(new Set());
  useEffect(() => {
    dispatchedKeysRef.current = new Set();
  }, [appSyncChannelId, appSyncScope, appSyncPoolId]);

  /**
   * Hydrate BuildTab / shared listeners from the persisted channel feed
   * (newest-first API → oldest-first dispatch). Guarded so React StrictMode's
   * double-invocation doesn't double-replay dozens of envelopes and swamp
   * ``workbenchRealtime`` dedupe — the downstream consumers are now also
   * idempotent via stable stage_id upserts, but skipping the second REST
   * burst keeps the Network panel quiet.
   */
  const channelHistoryLoadedRef = useRef(null);
  useEffect(() => {
    if (!appSyncChannelId || !appSyncScope) return undefined;
    const cacheKey = `${appSyncChannelId}::${appSyncPoolId || ''}`;
    if (channelHistoryLoadedRef.current === cacheKey) return undefined;
    channelHistoryLoadedRef.current = cacheKey;

    let cancelled = false;
    (async () => {
      try {
        const { events } = await fetchNetworkChannelEvents(appSyncChannelId, {
          limit: 80,
          poolId: appSyncPoolId,
        });
        if (cancelled || !events.length) return;
        const chronological = [...events].reverse();
        for (let i = 0; i < chronological.length; i += 1) {
          const raw = chronological[i];
          const normalized = normalizeAppSyncWorkbenchPayload(raw);
          if (normalized) {
            dispatchWorkbenchRealtime(normalized, 'appsync');
          }
        }
      } catch (e) {
        // Reset on failure so a manual refresh / rerender can retry.
        if (channelHistoryLoadedRef.current === cacheKey) {
          channelHistoryLoadedRef.current = null;
        }
        console.warn('[NetworkWorkbench] channel-events history load skipped:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appSyncChannelId, appSyncScope, appSyncPoolId]);

  useEffect(() => {
    const seen = dispatchedKeysRef.current;
    for (let i = 0; i < appSyncMessages.length; i += 1) {
      const raw = appSyncMessages[i];
      // Key-based cursor: skip only envelopes we've ALREADY dispatched (survives a
      // front-trim that shifts indices). Falls back to index when no stable key.
      const key = agentChannelEventKey(raw) || `idx:${i}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const normalized = normalizeAppSyncWorkbenchPayload(raw);
      if (normalized) {
        dispatchWorkbenchRealtime(normalized, 'appsync');
        window.dispatchEvent(
          new CustomEvent('appsync-agent-message', { detail: raw }),
        );
      }
    }
    // Bound the ledger so a long session doesn't grow it unbounded (matches the channel's
    // own soft-cap philosophy). Keep well above SOFT_CAP so nothing in the live buffer is
    // ever forgotten-then-redispatched.
    if (seen.size > 4000) dispatchedKeysRef.current = new Set();
  }, [appSyncMessages]);

  // Realtime listener for collaborator-initiated restores. The restore
  // handler publishes a ``FIELD_UPDATED`` beacon with
  // ``payload.changeKind === 'NETWORK_RESTORED'`` (see rest_handlers
  // ``restore_network_handler``); recipients are expected to silently
  // re-fetch the network so the agents map / runtime config / log level
  // converge across editors. The decision logic is extracted into
  // ``evaluateRestoreEvents`` so Vitest can cover the dedupe + actor
  // classification without booting an AppSync mock.
  const restoreEventSeenRef = useRef(new Set());
  useEffect(() => {
    const myNetworkId = currentNetworkRef.current?.networkId;
    if (!myNetworkId) return;
    const seen = restoreEventSeenRef.current;
    const { shouldRefetch, byOther, newSeenIds } = evaluateRestoreEvents(
      workbenchChannelEvents,
      myNetworkId,
      user?.sub,
      seen,
    );
    if (!shouldRefetch) return;
    newSeenIds.forEach((id) => seen.add(id));
    // The local restore path already calls ``fetchNetwork`` via
    // ``onRestored`` on SnapshotsTab; this branch covers the
    // collaborator case where the publish arrives with no local
    // dialog round-trip. Calling ``fetchNetwork`` twice in the local
    // case is harmless (silent) and converges to the same state.
    fetchNetwork(myNetworkId, { silent: true });
    if (byOther) {
      showToast('Network was restored by another collaborator', 'success');
    }
    // Cap the seen-set so it doesn't grow unbounded over a long session.
    if (seen.size > 200) {
      restoreEventSeenRef.current = new Set();
    }
  }, [workbenchChannelEvents, user?.sub, showToast]);

  /** PR5 — collaborator secret-registry edits (NETWORK scoped) fan FIELD_UPDATED. */
  const secretRegistryWbIdxRef = useRef(0);
  useEffect(() => {
    secretRegistryWbIdxRef.current = 0;
  }, [networkId, workbenchTenantId]);
  useEffect(() => {
    const events = workbenchChannelEvents || [];
    const start = secretRegistryWbIdxRef.current;
    if (start >= events.length) return;
    for (let i = start; i < events.length; i += 1) {
      const evt = events[i];
      const et = String(evt?.eventType || '').toUpperCase();
      if (et !== 'FIELD_UPDATED') continue;
      const pl = evt?.payload || {};
      if (String(pl.changeKind || '').toUpperCase() !== 'SECRET_REGISTRY') continue;
      window.dispatchEvent(
        new CustomEvent('stepflow-secret-registry-changed', {
          detail: {
            source: 'workbench-channel',
            networkId,
            payload: pl,
          },
        }),
      );
    }
    secretRegistryWbIdxRef.current = events.length;
  }, [workbenchChannelEvents, networkId]);

  // State
  const [currentNetwork, setCurrentNetwork] = useState(null);
  // Releases list — used by ``WorkbenchHeader`` to render the
  // ``MAJOR.MINOR`` label (e.g. "1.10") on the Released chip. Fetched
  // once on mount + refreshed when a release event fires.
  const [networkReleases, setNetworkReleases] = useState([]);
  // Cold mount: ``loading=true`` so we show the spinner while the
  // first ``GET /networks/{id}`` is in flight.
  // Hot mount via ``handleNetworkCreated`` (BuildTab just POSTed and
  // we're navigating ``/network/new`` → ``/network/<newId>``):
  // ``location.state.workbenchPostCreate.network`` carries the freshly
  // created row, so we can seed ``currentNetwork`` synchronously and
  // skip the spinner. App.jsx's ``RouteErrorBoundary`` is keyed on
  // ``pathname`` and unmounts NetworkWorkbench on every navigate,
  // so without this seeding the new mount would flash "Loading
  // workbench..." in between the user hitting Enter and AppSync
  // resuming the stream.
  const _bootstrap = location.state?.workbenchPostCreate?.network;
  const _bootstrapMatches =
    _bootstrap?.networkId
    && networkId
    && networkId !== 'new'
    && _bootstrap.networkId === networkId;
  const [loading, setLoading] = useState(() => !(_bootstrapMatches || networkId === 'new'));
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [saveSnapshotOpen, setSaveSnapshotOpen] = useState(false);
  // Gated delete flow — opening this state mounts the
  // ``DeleteNetworkDialog`` which fetches the impact preview itself. The
  // raw window.confirm path was removed in favor of the type-to-confirm
  // dialog so a misclick on the header menu can no longer delete a
  // network with active deployments.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Bumped whenever the lifted SaveSnapshotDialog reports success so
  // ``SnapshotsTab`` re-fetches the list even when it's the inactive
  // tab (``unmount={false}`` keeps it mounted but its own local refresh
  // tick no longer ticks after we dropped its internal Save button).
  const [snapshotRefreshSignal, setSnapshotRefreshSignal] = useState(0);
  // ``SnapshotsTab`` publishes the newest user-snapshot's metadata so
  // ``WorkbenchHeader`` can render the "Last snapshot · 12m ago" helper
  // line next to the Save button. ``null`` means there are no user
  // snapshots yet (clean first-run state → header hides the label).
  const [latestUserSnapshot, setLatestUserSnapshot] = useState(null);
  const handleLatestUserSnapshotChange = useCallback((payload) => {
    setLatestUserSnapshot(payload || null);
  }, []);

  // When the sidebar kebab navigates here with ``state.openRelease`` /
  // ``state.openSaveSnapshot``, pop the corresponding dialog once and
  // scrub the state so a subsequent refresh doesn't re-open it.
  useEffect(() => {
    const wantRelease = location.state?.openRelease;
    const wantSaveSnapshot = location.state?.openSaveSnapshot;
    if (!wantRelease && !wantSaveSnapshot) return;
    if (!networkId || networkId === 'new') return;
    if (wantRelease) setReleaseOpen(true);
    if (wantSaveSnapshot) setSaveSnapshotOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, networkId, navigate]);

  // Latest network for fetch gating (avoids stale closures without re-running fetch on every parent update)
  const currentNetworkRef = useRef(currentNetwork);
  currentNetworkRef.current = currentNetwork;

  /** After POST /networks, the route updates before ref/state may match — avoid full-page loading (which hides all tabs). */
  const skipFetchLoadingForNetworkIdRef = useRef(null);
  // When BuildTab POSTs ``/networks`` and immediately hands us the freshly
  // created row, the network payload is already complete — chatHistory and
  // latestGraph are empty for brand-new networks and the AppSync stream is
  // about to deliver every Brain event live. Re-fetching ``GET /networks/{id}``
  // right after navigation only forces a ``setCurrentNetwork`` replacement
  // mid-stream, which re-renders the workbench subtree and visibly interrupts
  // message streaming. This ref tells the route-change effect to skip the
  // fetch entirely once per matching id.
  const skipFetchEntirelyForNetworkIdRef = useRef(null);
  const { resetRunner } = useAppStore();

  /** Brain build is REST-initiated — progress events stream back over AppSync.
   *  ``opts`` carries build toggles (e.g. generateNetworkSkills) to the SFN. */
  const triggerStartDeployment = useCallback(async (prompt, networkId, opts = {}) => {
    try {
      await startBrainDeployment(networkId, prompt, opts);
    } catch (e) {
      console.error('[NetworkWorkbench] startBrainDeployment failed:', e);
      // Surface failure to BuildTab listeners so spinners clear.
      dispatchWorkbenchRealtime(
        {
          type: 'status_update',
          status: 'FAILED',
          taskId: '',
          payload: { error: e?.message || String(e) },
          timestamp: new Date().toISOString(),
        },
        'rest-error',
      );
    }
  }, []);

  // Post-create bootstrap: BuildTab POSTed ``/networks`` and is
  // navigating ``/network/new`` → ``/network/<newId>``. App.jsx's
  // route-key remounts us, but the parent stuffed the freshly-created
  // row into ``location.state.workbenchPostCreate`` so we can paint
  // synchronously instead of flashing the "Loading workbench" spinner
  // while a redundant GET /networks/{id} runs (the row we'd fetch is
  // identical to what we just POSTed).
  useLayoutEffect(() => {
    const seeded = location.state?.workbenchPostCreate?.network;
    const buildPrompt = location.state?.workbenchPostCreate?.buildPrompt;
    if (!seeded?.networkId || seeded.networkId !== networkId || networkId === 'new') {
      return;
    }
    setCurrentNetwork({
      ...seeded,
      chatHistory: buildPrompt ? [{ role: 'user', content: buildPrompt, timestamp: new Date().toISOString() }] : [],
      latestGraph: null,
    });
    setLoading(false);
    skipFetchEntirelyForNetworkIdRef.current = networkId;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, networkId, navigate]);

  // Import & deploy: parent passes the 200 response in router state so we
  // can paint the header + tabs immediately; ``GET /networks/{id}`` still
  // runs right after (silent) to merge S3 chat + latest graph.
  useLayoutEffect(() => {
    const pack = location.state?.workbenchImportBootstrap;
    const n = pack?.network;
    if (!n?.networkId || n.networkId !== networkId || networkId === 'new') {
      return;
    }
    setCurrentNetwork({
      ...n,
      chatHistory: [],
      latestGraph: null,
    });
    setLoading(false);
    skipFetchLoadingForNetworkIdRef.current = networkId;
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, networkId, navigate]);

  // Fetch specific network when ID changes.
  //
  // Use ``user?.sub`` (stable JWT claim) instead of ``user`` (object identity)
  // so AuthContext re-renders that don't actually change *who* is logged in
  // (workspace session echo, stepflowRole patch, billing refresh, …) don't
  // pile up a ``GET /networks/{id}`` per render. Without this guard, every
  // sidebar click would land 10+ duplicate fetches before the response from
  // the first one came back.
  useEffect(() => {
    if (user?.sub && networkId && networkId !== 'new') {
      // Skip the fetch entirely when BuildTab just created this network and
      // handed us the full row — re-fetching mid-stream visibly interrupts
      // AppSync message flow. Cleared after one match so a later mount /
      // hard refresh still hydrates from server.
      if (skipFetchEntirelyForNetworkIdRef.current === networkId) {
        skipFetchEntirelyForNetworkIdRef.current = null;
        setLoading(false);
        return;
      }
      // Silent refetch when we already hold this network (e.g. navigate /new → /:id after POST) so the shell
      // does not unmount BuildTab — preserves in-session messages/stages while still loading chatHistory, etc.
      let silent = currentNetworkRef.current?.networkId === networkId;
      if (!silent && skipFetchLoadingForNetworkIdRef.current === networkId) {
        silent = true;
        skipFetchLoadingForNetworkIdRef.current = null;
      }
      fetchNetwork(networkId, { silent });
    } else if (networkId === 'new') {
      // Handle "new network" creation mode
      setLoading(false);
      setCurrentNetwork({
        networkId: 'new',
        name: 'New Network',
        description: '',
        status: 'DRAFT',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  }, [user?.sub, networkId]);

  useEffect(() => {
    if (canBuild) return;
    if (!currentNetwork || currentNetwork.networkId === 'new') return;
    if (![0, 1, 2, 5].includes(selectedTab)) return;
    const deployed = (currentNetwork.status || '').toString().toUpperCase() === 'ACTIVE';
    setSelectedTab(deployed ? 3 : 4);
  }, [canBuild, currentNetwork?.networkId, currentNetwork?.status, selectedTab]);

  // The Run tab's "Manage in Data Plane" link dispatches this event so the
  // user lands on the catalog without having to know tab indices. Scoped
  // to the matching networkId so a stale RunnerView in another workbench
  // can't hijack the focus.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onJump = (e) => {
      const eventNetworkId = e?.detail?.networkId;
      if (!eventNetworkId || !currentNetwork?.networkId) return;
      if (eventNetworkId !== currentNetwork.networkId) return;
      setSelectedTab(5); // Data Plane
    };
    window.addEventListener('stepflow:open-data-plane-tab', onJump);
    return () => window.removeEventListener('stepflow:open-data-plane-tab', onJump);
  }, [currentNetwork?.networkId]);

  // Keep title in sync when the sidebar renames the current network
  useEffect(() => {
    if (!currentNetwork?.networkId || currentNetwork.networkId === 'new') return;
    const row = networks.find((n) => n.networkId === currentNetwork.networkId);
    if (row && row.name !== currentNetwork.name) {
      setCurrentNetwork((prev) => (prev ? { ...prev, name: row.name } : prev));
    }
  }, [networks, currentNetwork?.networkId, currentNetwork?.name]);

  // Approval rehydration is owned end-to-end by ``BuildTab.fetchExecutions``
  // (REST ``/networks/{id}/executions`` + ``…/executions/{taskId}`` →
  // ``restoreApprovalFromDetails``). Historically this page *also* synthesised a
  // ``status_update APPROVAL_REQUIRED`` from ``/sync-state`` on every mount with
  // a fresh timestamp, which defeated ``workbenchRealtime`` dedupe and raced the
  // channel-events replay + the live AppSync envelope — producing the
  // duplicated Approval panels the user reported. BuildTab is the single
  // authoritative rehydration path; this effect intentionally removed.

  // Clear global runner state when switching networks (prevents stale logs, output, etc.)
  useEffect(() => {
    resetRunner();
  }, [networkId, resetRunner]);

  // Fetch the releases list once per network so the WorkbenchHeader can
  // render the ``MAJOR.MINOR`` label on the Released chip. Refreshed when
  // the network's ``releaseVersion`` changes (a new release was cut) or
  // when ``snapshotRefreshSignal`` ticks (lifecycle action invalidated
  // the cache).
  useEffect(() => {
    if (!networkId || networkId === 'new') {
      setNetworkReleases([]);
      return undefined;
    }
    let cancelled = false;
    listNetworkReleases(networkId, { limit: 50 })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.releases) ? data.releases : [];
        setNetworkReleases(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[NetworkWorkbench] listNetworkReleases failed:', err);
        setNetworkReleases([]);
      });
    return () => { cancelled = true; };
  }, [networkId, currentNetwork?.releaseVersion]);

  const fetchNetwork = async (id, options = {}) => {
    const { silent = false, _retryIfNoGraph = false } = options;
    if (!silent) setLoading(true);
    try {
      const token = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentNetwork({
          ...data.network,
          chatHistory: data.chatHistory || [],
          latestGraph: data.latestGraph || null,
          // ``builds`` / ``latestBuild`` are response SIBLINGS of ``network``
          // (like ``latestGraph``), not nested inside it — carry them through
          // so the Build box can hydrate the GENERATED prompts + skills + usage
          // from the immutable per-revision manifest. Dropping them was why the
          // skills deck vanished (reducer fell back to the skill-less AGENT rows).
          builds: data.builds || [],
          latestBuild: data.latestBuild || null
        });
        // Archiver writes the ASL after visualizer broadcasts SUCCESS, so the
        // first fetch can race and return latestGraph=null. Retry once after
        // 3s so the final diagram appears without a manual refresh.
        if (_retryIfNoGraph && !data.latestGraph) {
          setTimeout(() => fetchNetwork(id, { silent: true }), 3000);
        }
      } else if (response.status === 404) {
        if (!silent) navigate('/network/new');
      }
    } catch (error) {
      console.error('Error fetching network:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ✅ Handle network creation from BuildTab — use React Router so useParams() stays in sync
  // (replaceState alone left route stale and broke BuildTab session + clear effects)
  const handleNetworkCreated = async (newNetwork) => {
    console.log('Network created, updating state:', newNetwork);

    // App.jsx's ``RouteErrorBoundary key={location.pathname}`` remounts
    // NetworkWorkbench whenever the URL changes — even from
    // ``/network/new`` to ``/network/<newId>``. The remount loses
    // ``currentNetwork`` and resets ``loading`` to its useState
    // initializer. We hand the freshly-created row through
    // ``location.state.workbenchPostCreate`` so the new mount can
    // paint the workbench synchronously instead of flashing the
    // "Loading workbench..." spinner mid-stream.
    setCurrentNetwork(newNetwork);
    await refreshNetworks();

    navigate(`/network/${newNetwork.networkId}`, {
      replace: true,
      state: { workbenchPostCreate: { network: newNetwork, buildPrompt: newNetwork._buildPrompt } },
    });
  };

  const handleRenameNetwork = async (id, newName) => {
    if (!newName?.trim()) return;
    const trimmed = newName.trim();
    try {
      const token = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmed })
      });
      if (response.ok) {
        refreshNetworks();
        if (currentNetwork?.networkId === id) {
          setCurrentNetwork({ ...currentNetwork, name: trimmed });
        }
      }
    } catch (error) {
      console.error('Error renaming network:', error);
    }
  };

  // Successful deletion comes back from ``DeleteNetworkDialog`` after the
  // server has wiped every adjacency row + S3 prefix. Navigate away first so
  // we leave the stale workbench route even if refresh/toast plumbing regresses,
  // then refresh the network list and show a toast. Errors inline in dialog.
  const handleNetworkDeleted = (res, deletedNetworkId) => {
    setDeleteOpen(false);
    const displayName =
      currentNetwork?.name || res?.networkId || deletedNetworkId;
    const wasCurrent = currentNetwork?.networkId === deletedNetworkId;
    if (wasCurrent) {
      navigate('/network/new');
    }
    refreshNetworks?.();
    refreshAgenticNetworks?.();
    showToast?.({
      type: 'success',
      title: 'Network deleted',
      message: res?.networkId
        ? `${displayName} has been permanently removed.`
        : 'Network has been permanently removed.',
    });
  };

  if (loading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
          <p className="text-gray-600">Loading workbench...</p>
        </div>
      </div>
    );
  }

  if (!currentNetwork) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-600">Network not found</p>
          <button
            type="button"
            onClick={() => navigate('/network/new')}
            className="mt-4 rounded-lg bg-blue-500 px-6 py-2 text-white hover:bg-blue-600"
          >
            New Network
          </button>
        </div>
      </div>
    );
  }

  const isDeployed = isNetworkDeployedForTabs(currentNetwork.status);
  // The release CTA is a pure function of (status, pendingReleaseChanges, releaseVersion).
  // Kept in utils/releaseBanner.js so the predicate + copy can be unit-locked
  // independently — see tests/features/ui-workbench/unit/releaseBanner.test.ts.
  // The old banner markup was removed (the button text is self-explanatory);
  // we now surface the same CTA as a primary button in WorkbenchHeader.
  const releaseBannerConfig = getReleaseBannerConfig(currentNetwork, { releases: networkReleases });
  const isRealNetwork =
    !!currentNetwork?.networkId && currentNetwork.networkId !== 'new';
  const showReleaseCta =
    !!releaseBannerConfig &&
    canBuild &&
    !!activeTenantId &&
    isRealNetwork;
  const canSaveSnapshot = canBuild && isDeployed && isRealNetwork;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        {/* (Global search lives in WorkbenchShell's top bar — not duplicated here.) */}
        {/* Header */}
        <WorkbenchHeader
          network={currentNetwork}
          canRename={canBuild && isRealNetwork}
          canDelete={canDeleteNw && isRealNetwork}
          canRelease={showReleaseCta}
          canSaveSnapshot={canSaveSnapshot}
          lastUserSnapshotAt={latestUserSnapshot?.capturedAt || null}
          lastUserSnapshotLabel={latestUserSnapshot?.label || null}
          releaseCtaLabel={
            releaseBannerConfig?.ctaLabel || 'Release to Agentic Networks'
          }
          presence={canBuild ? workbenchPresence : null}
          currentUserSub={user?.sub || ''}
          // Live-connection dot = EITHER live AppSync subscription is up: the
          // agent/build channel (always on — the one that logs "subscribed ·
          // /default/agent/…") OR the collab presence channel (off on Build tab).
          // Previously only tracked collab → dot was grey on the Build tab even
          // though the agent channel was subscribed (the out-of-sync the user saw).
          workbenchConnected={appSyncConnected || workbenchConnected}
          releases={networkReleases}
          onUpdateNetwork={(updates) => {
            if (updates.name !== undefined) {
              handleRenameNetwork(currentNetwork.networkId, updates.name);
            } else {
              setCurrentNetwork({ ...currentNetwork, ...updates });
            }
          }}
          onDelete={() => setDeleteOpen(true)}
          onRelease={() => setReleaseOpen(true)}
          onSaveSnapshot={() => setSaveSnapshotOpen(true)}
        />

        {/* The explanatory release banner was removed — its CTA lives as a
            primary button in WorkbenchHeader (same qualification via
            ``releaseBannerConfig``); the button copy is self-explanatory. */}

        {/* Tabs: workflow (Build · Agents · Run) left, secondary (Usage · Settings) right */}
        {/* Tab.Group must fill column flex + tab strip must not shrink, or the strip collapses and tabs vanish */}
        <Tab.Group
          as="div"
          selectedIndex={selectedTab}
          onChange={setSelectedTab}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          <div className="min-w-0 shrink-0 border-b border-gray-200 bg-white px-3 sm:px-4">
            <Tab.List className="-mb-px flex w-full min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 overflow-x-auto pb-px sm:gap-x-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 sm:gap-x-2">
                <Tab
                  disabled={!canBuild}
                  className={({ selected }) =>
                    `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <Hammer className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Build
                </Tab>
                <Tab
                  disabled={!canBuild || !currentNetwork?.networkId || currentNetwork.networkId === 'new'}
                  className={({ selected, disabled }) =>
                    `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <ListChecks className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Executions
                </Tab>
                <Tab
                  disabled={!canBuild || !isDeployed}
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <Bot className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Agents
                  {(!canBuild || !isDeployed) && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 block -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                      role="tooltip"
                    >
                      Deploy successfully to configure agents
                      <span className="absolute top-full left-1/2 -mt-1 block -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </span>
                  )}
                </Tab>
                <Tab
                  disabled={!isDeployed}
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <Play className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Run
                  {!isDeployed && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 block -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                      role="tooltip"
                    >
                      Deploy successfully to unlock the Runner
                      <span className="absolute top-full left-1/2 -mt-1 block -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </span>
                  )}
                </Tab>
                <Tab
                  disabled={!isDeployed}
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <GitBranch className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Lifecycle
                  {!isDeployed && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 block -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                      role="tooltip"
                    >
                      Deploy successfully to manage the network lifecycle
                      <span className="absolute top-full left-1/2 -mt-1 block -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </span>
                  )}
                </Tab>
                <Tab
                  disabled={!isDeployed}
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <Database className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Context
                  {!isDeployed && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 block -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                      role="tooltip"
                    >
                      Deploy successfully to manage Context assets
                      <span className="absolute top-full left-1/2 -mt-1 block -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </span>
                  )}
                </Tab>
                <Tab
                  disabled={!isDeployed}
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                >
                  <Cable className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Connections
                  {!isDeployed && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 block -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                      role="tooltip"
                    >
                      Deploy successfully to manage external API connections and credentials
                      <span className="absolute top-full left-1/2 -mt-1 block -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </span>
                  )}
                </Tab>
                <Tab
                  className={({ selected, disabled }) =>
                    `group relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                  disabled={!currentNetwork?.networkId || currentNetwork.networkId === 'new'}
                >
                  <Sparkles className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Skills
                </Tab>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-1 gap-y-1 sm:gap-x-2">
                <Tab
                  className={({ selected, disabled }) =>
                    `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                  disabled={!currentNetwork?.networkId || currentNetwork.networkId === 'new'}
                >
                  <BarChart3 className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Usage
                </Tab>
                <Tab
                  className={({ selected, disabled }) =>
                    `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                  disabled={!canBuild || !currentNetwork?.networkId || currentNetwork.networkId === 'new'}
                >
                  <Settings className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Settings
                </Tab>
                {/* Self-improvement (generative networks): health report + rate + the
                    propose→revise loop. Appended last so no existing TAB_INDEX shifts. */}
                <Tab
                  className={({ selected, disabled }) =>
                    `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                      selected
                        ? 'border-blue-500 text-blue-600'
                        : disabled
                          ? 'cursor-not-allowed border-transparent text-gray-400'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                    }`
                  }
                  disabled={!currentNetwork?.networkId || currentNetwork.networkId === 'new'}
                >
                  <Wand2 className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                  Improve
                </Tab>
              </div>
            </Tab.List>
          </div>

          <Tab.Panels className="flex-1 min-h-0 overflow-hidden flex flex-col" unmount={false}>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <BuildTab
                network={currentNetwork}
                selectedNetworkId={networkId}
                seedPrompt={reviseSeedPrompt}
                onTriggerStartDeployment={triggerStartDeployment}
                onNetworkUpdate={(updates) => {
                  // Idempotency guard: BuildTab fires this on every SUCCESS /
                  // EXECUTION_COMPLETED envelope it sees, and the
                  // ``channel-events`` history endpoint replays ~80 of those on
                  // mount (one per past run). Without this guard each replay
                  // queues ``GET /networks/{id}`` + ``GET /networks`` against
                  // the same-origin throttle, producing the visible 300 ms
                  // ping-pong cascade. We refetch only on a *real* state
                  // transition (e.g. DRAFT → ACTIVE), and we never refetch
                  // when the request is just re-asserting the status we
                  // already hold for this network.
                  if (updates.networkId && (!currentNetwork || currentNetwork.networkId === 'new')) {
                    handleNetworkCreated(updates);
                    return;
                  }
                  const curStatus = String(currentNetwork?.status || '').toUpperCase();
                  const nextStatus = String(updates.status || '').toUpperCase();
                  if (nextStatus === 'ACTIVE') {
                    const id = updates.networkId || currentNetwork?.networkId;
                    if (curStatus === 'ACTIVE' || curStatus === 'RELEASED') {
                      if (updates._brainCompleted && id) {
                        fetchNetwork(id, { silent: true, _retryIfNoGraph: true });
                      }
                      return;
                    }
                    if (id) {
                      fetchNetwork(id, { silent: true, _retryIfNoGraph: true });
                      refreshNetworks();
                    } else {
                      setCurrentNetwork(prev => ({ ...prev, ...updates }));
                      refreshNetworks();
                    }
                    return;
                  }
                  // Other updates (rename, description, etc.). Skip the
                  // sidebar refresh when nothing user-visible changes there.
                  setCurrentNetwork(prev => ({ ...prev, ...updates }));
                  if (updates.name || updates.description || updates.status) {
                    refreshNetworks();
                  }
                }}
              />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <WorkbenchExecutionsTab networkId={networkId} />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <AgentsTab
                network={currentNetwork}
                workbenchEvents={workbenchChannelEvents}
                onNavigateToConnections={navigateToConnections}
                pendingToolAssign={pendingToolAssign}
                onPendingToolAssignConsumed={() => setPendingToolAssign(null)}
                onAgentUpdate={(updatedAgents) => {
                  setCurrentNetwork(prev => ({ ...prev, agents: updatedAgents }));
                  refreshNetworks();
                  if (currentNetwork?.networkId) {
                    fetchNetwork(currentNetwork.networkId, { silent: true });
                  }
                }}
              />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <RunTab network={currentNetwork} />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <LifecycleTab
                network={currentNetwork}
                workspaceRole={workspaceRole}
                refreshSignal={snapshotRefreshSignal}
                onLatestUserSnapshotChange={handleLatestUserSnapshotChange}
                onNetworkUpdate={(updates) => {
                  setCurrentNetwork((prev) => ({ ...prev, ...updates }));
                  refreshNetworks();
                }}
                onRestored={() => {
                  // Mirror the release-success path: re-fetch the
                  // network so the agent dropdown reflects the restored
                  // state without a hard browser refresh.
                  if (currentNetwork?.networkId) {
                    fetchNetwork(currentNetwork.networkId, { silent: true });
                  }
                  refreshNetworks();
                  refreshAgenticNetworks?.();
                }}
              />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <DataPlaneTab network={currentNetwork} />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white outline-none">
              {activeTenantId ? (
                <SecretsCatalog
                  tenantId={activeTenantId}
                  catalogNetworkId={currentNetwork?.networkId}
                  workspaceRole={workspaceRole}
                  viewerUserSub={user?.sub}
                  networkOwnerUserId={String(currentNetwork?.userId || '').trim()}
                  autoOpenTool={connectionsAutoOpenTool}
                  onAutoOpenConsumed={() => setConnectionsAutoOpenTool(null)}
                  onConnectionCreated={handleConnectionCreated}
                />
              ) : null}
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white outline-none">
              {activeTenantId && currentNetwork?.networkId && currentNetwork.networkId !== 'new' ? (
                <SkillsCatalog
                  tenantId={activeTenantId}
                  registryScope={{ kind: 'NETWORK', networkId: currentNetwork.networkId }}
                />
              ) : null}
            </Tab.Panel>
            <Tab.Panel className="flex-1 min-h-0 overflow-hidden">
              <UsageTab network={currentNetwork} />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <SettingsTab
                network={currentNetwork}
                workspaceRole={workspaceRole}
                onNetworkUpdate={(updates) => {
                  setCurrentNetwork((prev) => ({ ...prev, ...updates }));
                  refreshNetworks();
                }}
              />
            </Tab.Panel>
            <Tab.Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <SelfImprovementTab
                network={currentNetwork}
                onReviseWithPrompt={(prompt) => {
                  // Hand the framed revise prompt to the Build tab (generative) and
                  // switch there — the user reviews + sends it through the normal
                  // revise → approve → effect route (§6b).
                  setReviseSeedPrompt(prompt);
                  setSelectedTab(0);
                }}
              />
            </Tab.Panel>
            </Tab.Panels>
        </Tab.Group>

        {isRealNetwork ? (
          <>
            <ReleaseNetworkDialog
              open={releaseOpen}
              onClose={() => setReleaseOpen(false)}
              networkId={currentNetwork.networkId}
              networkName={currentNetwork.name || currentNetwork.networkId}
              networkReleaseVersion={currentNetwork.releaseVersion}
              network={currentNetwork}
              onReleased={() => {
                setReleaseOpen(false);
                refreshAgenticNetworks?.();
                refreshNetworks();
                fetchNetwork(currentNetwork.networkId, { silent: true });
              }}
            />
            {saveSnapshotOpen ? (
              <SaveSnapshotDialog
                open
                networkId={currentNetwork.networkId}
                onClose={() => setSaveSnapshotOpen(false)}
                onSaved={() => {
                  setSaveSnapshotOpen(false);
                  setSnapshotRefreshSignal((t) => t + 1);
                }}
              />
            ) : null}
            <DeleteNetworkDialog
              open={deleteOpen}
              networkId={currentNetwork.networkId}
              networkName={currentNetwork.name || currentNetwork.networkId}
              onClose={() => setDeleteOpen(false)}
              onDeleted={(res) =>
                handleNetworkDeleted(res, currentNetwork.networkId)
              }
            />
          </>
        ) : null}
    </div>
  );
}
